/**
 * FRIDGE-1497 — how much of a product photo is product.
 *
 * Some stored photos are mostly white canvas with the appliance drawn small in
 * the middle. On the homepage freezer rail (production 10/16/2026, 96 card
 * photos) one did: the Frigidaire chest freezer FIN-0017740, whose product
 * spans 29% of its 384 px photo, so the card showed a small white box while
 * its neighbours filled their frames (owner, 10/13/2026: "this image sucks").
 *
 * `contentBox` reads the photo's non-white bounding box from a small canvas
 * copy (same-origin images only; a tainted canvas returns null).
 * `fillTransform` turns it into the zoom and shift that make the product fill
 * the frame like its neighbours, for photos under LOW_FILL only, and never
 * further than the frame allows: the product stays whole and centred.
 */

/** A photo whose product spans less than this share of it is zoomed. */
export const LOW_FILL = 0.6
/** The share of the frame the zoomed product should span. */
export const TARGET_FILL = 0.86
/** Never zoom further than this. */
export const MAX_ZOOM = 3

export interface ContentBox {
  /** Product bounds as fractions of the photo, 0 to 1. */
  x0: number
  y0: number
  x1: number
  y1: number
}

/** The product's bounding box in a photo: pixels that are neither near-white nor transparent. */
export function contentBoxFromPixels(data: Uint8ClampedArray | number[], size: number, whiteAt = 242): ContentBox | null {
  let x0 = size, y0 = size, x1 = -1, y1 = -1
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4
      if (data[i + 3] < 16) continue
      if (data[i] > whiteAt && data[i + 1] > whiteAt && data[i + 2] > whiteAt) continue
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
    }
  }
  if (x1 < 0) return null
  return { x0: x0 / size, y0: y0 / size, x1: (x1 + 1) / size, y1: (y1 + 1) / size }
}

/** Read a loaded <img>'s content box through a small canvas; null when unreadable or blank. */
export function contentBox(img: HTMLImageElement, size = 64): ContentBox | null {
  try {
    if (!img.naturalWidth || !img.naturalHeight) return null
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, size, size)
    return contentBoxFromPixels(ctx.getImageData(0, 0, size, size).data, size)
  } catch {
    return null // a cross-origin photo taints the canvas: leave it as it is
  }
}

/**
 * The CSS transform that makes a low-fill product fill its frame, or null.
 * `frame` is the drawing area (the img box less its padding), `natural` the
 * photo's own size; object-fit: contain draws the photo centred in the frame.
 * `border` is the img's own box, the reference for the translate percentages,
 * so the product stays centred if the card is resized after the photo loads.
 */
export function fillTransform(box: ContentBox, natural: { w: number; h: number }, frame: { w: number; h: number }, border: { w: number; h: number } = frame): string | null {
  const fill = Math.max(box.x1 - box.x0, box.y1 - box.y0)
  if (!(fill > 0) || fill >= LOW_FILL || frame.w <= 0 || frame.h <= 0) return null
  // The photo as drawn, and the product inside it, in px.
  const s = Math.min(frame.w / natural.w, frame.h / natural.h)
  const dw = natural.w * s, dh = natural.h * s
  const ox = (frame.w - dw) / 2, oy = (frame.h - dh) / 2
  const pw = (box.x1 - box.x0) * dw, ph = (box.y1 - box.y0) * dh
  const pcx = ox + ((box.x0 + box.x1) / 2) * dw, pcy = oy + ((box.y0 + box.y1) / 2) * dh
  const zoom = Math.min(MAX_ZOOM, (TARGET_FILL * frame.w) / pw, (TARGET_FILL * frame.h) / ph)
  if (zoom <= 1.05) return null
  // Scale about the frame centre, then move the product's centre onto it.
  const tx = ((frame.w / 2 - pcx) * zoom) / border.w * 100, ty = ((frame.h / 2 - pcy) * zoom) / border.h * 100
  return `translate(${tx.toFixed(2)}%, ${ty.toFixed(2)}%) scale(${zoom.toFixed(3)})`
}
